import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Label } from '@src/design-system/label';
import { cn } from '@src/design-system/helpers';

import { BriefCaseIcon } from '@shared/assets/icons/shared/brief-case-icon';
import { EmailIcon } from '@shared/assets/icons/shared/email-icon';
import { CompanyIcon } from '@shared/assets/icons/shared/company-icon';

import { Fragment, type ReactNode, type ComponentProps } from 'react';

export function InterviewerInfo<T>({
  className,
  avatar,
  id,
  jobTitle,
  email,
  companyName,
  name,
  item,
  renderExtraElementInside,
  renderExtraElementOutside,
  ...props
}: {
  id: number;
  avatar: string;
  name: string;
  jobTitle: string;
  email: string;
  companyName: string;
  item?: T;
  renderExtraElementInside?: (item?: T) => ReactNode;
  renderExtraElementOutside?: (item?: T) => ReactNode;
} & Omit<ComponentProps<'div'>, 'id'>) {
  const INTERVIEWER_INFO = [
    {
      label: jobTitle,
      icon: (
        <BriefCaseIcon
          height={16}
          width={16}
        />
      ),
    },
    {
      label: email,
      icon: <EmailIcon />,
    },
    {
      label: companyName,
      icon: <CompanyIcon />,
    },
  ];
  return (
    <div className='relative group'>
      <div
        className={cn(
          'flex items-center cursor-pointer gap-3 border border-pale-border p-2 rounded-xl',
          className
        )}
        {...props}
      >
        <Avatar className='size-10'>
          <AvatarImage src={avatar} />
          <AvatarFallback colorId={id}>{name?.substring(0, 1)}</AvatarFallback>
        </Avatar>
        <div className='w-full flex-1'>
          <Label className='mb-1'>{name}</Label>
          <div className='flex items-center w-[90%] text-gray-600 text-sm'>
            {INTERVIEWER_INFO.map((info) => {
              if (!info.label) return null;
              return (
                <Fragment key={info.label}>
                  <div className='size-[16px] mr-1'>{info.icon}</div>
                  <span
                    title={info.label}
                    className={cn(
                      'whitespace-nowrap',
                      info.label !== jobTitle && 'truncate'
                    )}
                  >
                    {info.label}
                  </span>
                  <span className='mx-2 last:hidden'>•</span>
                </Fragment>
              );
            })}
          </div>
        </div>
        {renderExtraElementInside?.(item)}
      </div>

      {renderExtraElementOutside?.(item)}
    </div>
  );
}
